import moment from 'moment';
import * as yup from 'yup';
import isEmpty from 'lodash.isempty';

const withRequired = (validation, required, message = 'Field is required') =>
  required ? validation.required(message) : validation;

export const getDefaultValues = ({ input_items }) => {
  const values = {};

  const getValueFromSimpleInput = question => {
    if (question.question_type === 'dynamic_list') {
      values[question.id] = [''];
    } else if (question.question_type === 'date') {
      values[question.id] = moment().format();
    } else {
      values[question.id] = null;
    }
  };

  const getValueFromGoals = ({ connected_questions }) => {
    connected_questions.forEach(({ questions, id }) => {
      values[id] = {};
      questions.forEach(item => (values[id][item.id] = null));
    });
  };

  const getValueFromConnected = ({ connected_questions }) => {
    connected_questions.forEach(({ questions, id }) => {
      values[id] = questions.reduce((value, item) => {
        const { id, question_type, question_answers } = item;
        if (question_type === 'fixed_radio') {
          value[id] = question_answers.reduce((radioValues, { id }) => {
            radioValues[id] = false;
            return radioValues;
          }, {});
          return value;
        }
        value[id] = '';
        return value;
      }, {});
    });
  };

  const getValueFromFixed = ({ questions }) =>
    questions.forEach(({ question_answers }) =>
      question_answers.forEach(
        ({ qa_type, preliminary_answer, additional_info, value, id }) => {
          if (qa_type === 'example') return (values[id] = additional_info);
          if (qa_type === 'preliminary' || preliminary_answer !== null)
            values[id] = preliminary_answer || value;
        }
      )
    );

  input_items.forEach(({ content }) => {
    content.question_set_type === 'goals' && getValueFromGoals(content);
    content.question_set_type === 'connected_question' &&
      getValueFromConnected(content);
    content.question_type && getValueFromSimpleInput(content);
    content.question_set_type &&
      content.question_set_type.startsWith('fixed_input') &&
      getValueFromFixed(content);
  });
  return values;
};

export const getValidationSchema = (data = {}) => {
  const schema = {};
  const {
    content: { input_items },
  } = data;

  const getFixedRadioValidation = content => {
    content.questions.forEach(
      ({ id, title, required }) =>
        (schema[id] = withRequired(
          yup.string().trim(),
          required,
          `${title} is required`
        ))
    );
  };

  const getRadioValidation = content => {
    content.questions.forEach(
      ({ id, required }) => (schema[id] = withRequired(yup.bool(), required))
    );
  };

  const getTextSetValidation = content =>
    content.questions.forEach(
      ({ id, required }) => (schema[id] = withRequired(yup.string(), required))
    );

  const getNumberSetValidation = content => {
    content.questions.forEach(
      ({
        id,
        required,
        question_type,
        min_value = Number.NEGATIVE_INFINITY,
        max_value = Number.MAX_VALUE,
      }) => {
        switch (question_type) {
          case 'text': {
            schema[id] = withRequired(yup.string(), required);
            return;
          }
          case 'number': {
            schema[id] = withRequired(
              yup
                .number()
                .typeError('Value must be a number')
                .min(min_value, `Value can not be less than ${min_value}`)
                .max(max_value, `Value can not be greater than ${max_value}`),
              required
            );
            return;
          }
          default:
            return;
        }
      }
    );
  };

  const getFixedInputValidation = content =>
    content.questions.forEach(
      ({
        question_answers,
        question_type,
        min_value = Number.NEGATIVE_INFINITY,
        max_value = Number.MAX_VALUE,
        show,
      }) => {
        if (show === null) return;
        question_answers.forEach(({ id, qa_type, required }) => {
          if (qa_type === 'preliminary') return;

          if (question_type === 'fixed_input_number') {
            schema[id] = withRequired(
              yup
                .number()
                .typeError('Value must be a number')
                .min(min_value, `Value can not be less than ${min_value}`)
                .max(max_value, `Value can not be greater than ${max_value}`),
              required
            );
            return;
          }
          schema[id] = withRequired(yup.string(), required);
        });
      }
    );

  const getDateValidation = ({ id, required }) => {
    schema[id] = withRequired(
      yup.date().max(new Date(), "You can't set a future date"),
      required
    );
  };

  const getCheckboxValidation = ({ questions }) => {
    questions.forEach(({ id, required }) => {
      schema[id] = withRequired(yup.boolean().nullable(), required);
    });
  };

  const getConnectedValidation = ({ connected_questions }) => {
    connected_questions.forEach(({ questions, id, required }) => {
      const shapeObject = questions.reduce((shape, item) => {
        if (item.question_type === 'text')
          shape[`${item.id}`] = withRequired(yup.string(), item.required);

        if (item.question_type === 'fixed_radio' && item.required) {
          shape[`${item.id}`] = yup
            .boolean()
            .transform(current =>
              Object.keys(current).some(key => current[key])
            )
            .oneOf([true], 'Field is required');
        }
        return shape;
      }, {});

      schema[id] = withRequired(yup.object(shapeObject), required);
    });
  };

  const getGoalsValidation = ({ connected_questions }) => {
    connected_questions.forEach(({ questions, id, required }) => {
      const shapeObject = {};
      questions.forEach(item => {
        if (item.question_type === 'text') {
          shapeObject[`${item.id}`] = yup.string().nullable();
        }
        if (item.question_type === 'integer') {
          shapeObject[`${item.id}`] = yup
            .number()
            .transform(current => (isNaN(current) ? undefined : current))
            .nullable()
            .typeError('Value must be a number')
            .min(1, 'Must be greater than or equal to 1')
            .max(365, 'Must be less than or equal to 365');
        }
      });
      schema[id] = withRequired(yup.object(shapeObject), required);
    });
  };

  const getDynamicValidation = ({ id, required }) => {
    if (required) {
      schema[id] = withRequired(
        yup
          .array()
          .compact()
          .min(1, 'At least one value is required'),
        required
      );
    }
  };

  input_items.forEach(({ content }) => {
    const { question_set_type, question_type } = content;
    question_set_type === 'goals' && getGoalsValidation(content);
    question_set_type === 'connected_question' &&
      getConnectedValidation(content);
    (question_set_type === 'fixed_input' ||
      question_set_type === 'fixed_input_related') &&
      getFixedInputValidation(content);
    question_set_type === 'fixed_input_preliminary' &&
      getFixedInputValidation(content);
    question_set_type === 'fixed_radio' && getFixedRadioValidation(content);
    question_set_type === 'checkbox' && getCheckboxValidation(content);
    question_set_type === 'radio' && getRadioValidation(content);
    question_set_type === 'text' && getTextSetValidation(content);
    question_set_type === 'number' && getNumberSetValidation(content);
    question_type === 'date' && getDateValidation(content);
    question_type === 'dynamic_list' && getDynamicValidation(content);
  });
  return yup.object().shape(schema);
};

export const getValuesFromCompleted = (completedData, content) => {
  const plainInputs = ['checkbox', 'radio', 'text', 'number'];
  const values = getDefaultValues(content);

  const putValueFromSimpleInput = (question, answer) => {
    if (
      (question.question_type === 'checkbox' ||
        question.question_type === 'radio') &&
      (answer === 't' || answer === 'f')
    ) {
      values[question.id] = answer === 't';
    } else {
      values[question.id] = answer === null ? '' : answer;
    }
  };

  const putValueFromFixedRadio = (inputs, question) => {
    inputs.forEach(({ value, question_answer_id }) => {
      if (value === 't') {
        values[question.id] = question_answer_id;
      }
    });
  };

  const putValueFromFixedInput = inputs =>
    inputs.forEach(({ value, question_answer_id }) => {
      values[question_answer_id] = value;
    });

  const putValueFromDeepInput = inputs => {
    inputs.forEach(({ question, answer, question_answer_progresses }) => {
      question.question_type === 'fixed_radio' &&
        putValueFromFixedRadio(question_answer_progresses, question);
      (question.question_type === 'fixed_input' ||
        question.question_type === 'fixed_input_number') &&
        putValueFromFixedInput(question_answer_progresses, question);
      question.question_type === 'fixed_input_preliminary' &&
        putValueFromFixedInput(question_answer_progresses, question);
      plainInputs.includes(question.question_type) &&
        putValueFromSimpleInput(question, answer);
    });
  };

  const putValueFromGoals = inputs => {
    inputs.forEach(({ connected_question: { id }, question_progresses }) => {
      const value = {};
      question_progresses.forEach(({ question, answer }) => {
        value[question.id] = answer;
      });
      values[id] = value;
    });
  };

  const putValueFromConnected = inputs => {
    inputs.forEach(({ connected_question: { id }, question_progresses }) => {
      const value = {};
      question_progresses.forEach(
        ({ question, question_answer_progresses, answer }) => {
          if (question_answer_progresses) {
            value[question.id] = question_answer_progresses.reduce(
              (values, { id, question_answer_id, value }) => {
                values[question_answer_id] = value === 't';
                return values;
              },
              {}
            );
            return;
          }

          value[question.id] = answer;
        }
      );
      values[id] = value;
    });
  };

  if (completedData) {
    const { input_item_progresses } = completedData.content_progress;
    input_item_progresses.forEach(({ content_progress }) => {
      const {
        question_progresses,
        connected_question_progresses,
        question_set,
        question,
        answer,
        answers_list,
      } = content_progress;
      if (answer || answers_list)
        putValueFromSimpleInput(question, answer || answers_list);

      if (question_progresses) putValueFromDeepInput(question_progresses);

      if (question_set && question_set.question_set_type === 'goals')
        putValueFromGoals(connected_question_progresses);

      if (
        question_set &&
        question_set.question_set_type === 'connected_question'
      )
        putValueFromConnected(connected_question_progresses);
    });
    return values;
  }
};

export const prepareValuesToSubmit = ({ data, values }) => {
  const getFixedTableValues = (id, questions) =>
    questions.map(question => ({
      id: question.id,
      value: values[id] === question.id || null,
    }));

  const getFixedInputValues = (id, questions, show) => {
    const getValue = id => (show === null ? null : values[id] || null);
    return questions.map(({ id }) => {
      return { id, value: getValue(id) };
    });
  };

  const getQuestionSetValues = questions => {
    return questions.map(({ id, question_answers, question_type, show }) => {
      if (question_type === 'fixed_radio') {
        return {
          id,
          question_answers: getFixedTableValues(id, question_answers),
        };
      }
      if (
        question_type === 'fixed_input' ||
        question_type === 'fixed_input_preliminary' ||
        question_type === 'fixed_input_number'
      ) {
        return {
          id,
          question_answers: getFixedInputValues(id, question_answers, show),
        };
      }

      return { id, answer: values[id] || null };
    });
  };

  const getConnectedInputValues = questions => ({
    connected_questions: questions.map(({ id, questions }) => ({
      id,
      questions: questions.map(item => {
        if (item.question_type === 'fixed_radio') {
          return {
            id: item.id,
            question_answers: item.question_answers.map(question => ({
              id: question.id,
              value: values[id][item.id][question.id],
            })),
          };
        }
        return {
          id: item.id,
          answer: values[id][item.id],
        };
      }),
    })),
  });

  const getGoalsValues = questions => ({
    connected_questions: questions.map(({ id, questions }) => ({
      id,
      questions: questions.map(item => ({
        id: item.id,
        answer: values[id][item.id],
      })),
    })),
  });

  const { content } = data;
  return content.input_items
    .map(({ content, id }) => {
      const { question_set_type } = content;
      if (question_set_type === 'satisfaction_info') {
        return { id };
      }

      if (!isEmpty(content.questions)) {
        return {
          id,
          question_set: getQuestionSetValues(content.questions),
        };
      }

      if (!isEmpty(content.connected_questions)) {
        const getValues =
          question_set_type === 'goals'
            ? getGoalsValues
            : getConnectedInputValues;

        return {
          id,
          question_set: getValues(content.connected_questions),
        };
      }

      if (content.question_type === 'dynamic_list') {
        return {
          id,
          question: {
            id: content.id,
            answers_list:
              (values &&
                values[content.id] &&
                values[content.id].filter(item => !!item)) ||
              [],
          },
        };
      }

      if (content.question_type) {
        return {
          id,
          question: { id: content.id, answer: values[content.id] || null },
        };
      }
      return null;
    })
    .filter(x => x);
};

export const postValidation = ({ data, values }) => {
  const {
    content: { input_items },
  } = data;
  const { additional_type, content } = input_items[0];
  if (additional_type === 'my_goal') {
    const goalsIds = content.connected_questions.map(({ id }) => id);
    const hasFilledGoal = !!goalsIds.find(goalId =>
      Object.keys(values[goalId]).find(id => values[goalId][id])
    );
    return !hasFilledGoal && 'At least one goal is required!';
  }
};



// WEBPACK FOOTER //
// ./src/containers/Module/utils.js